import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  alpha,
  useTheme,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayIcon from '@mui/icons-material/Replay';
import MovieIcon from '@mui/icons-material/Movie';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DownloadIcon from '@mui/icons-material/Download';
import Hls from 'hls.js';
import { monitoringWebSocket } from '../../services/websocketService';
import { getBearerTokenForApi } from '../../utils/poolSiteAuth';
import {
  fetchEventRiskClip,
  getRiskEvidenceDownloadUrl,
  getRiskEvidenceMp4Url,
  getRiskEvidencePlaylistUrl,
  isDrowningEventType,
  normalizePlaybackUrl,
  type RiskEvidenceManifest,
} from '../../services/riskClipService';
import {
  DEFAULT_RISK_RECORDING_SETTINGS,
  fetchRiskRecordingSettingsFromSystem,
  type RiskRecordingSettings,
} from '../../constants/riskRecordingSettings';

export type RiskEvidencePlayerProps = {
  eventId: number | string;
  eventType?: string;
  cctvId?: number;
  poolName?: string;
  locationLabel?: string;
  eventTimestamp?: string | number | Date;
  onStatusChange?: (status: 'ready' | 'archiving' | 'preview' | 'unavailable' | 'not_applicable') => void;
  /** 카드 없이 영역만 채울 때 */
  embedded?: boolean;
  minHeight?: number;
  /** 수집·로딩 중 배경으로 쓸 감지 시점 정지 화면 */
  posterImageUrl?: string;
  /** true면 수집·로딩 중에는 포스터(스크린샷)만 표시하고 수집 안내 UI는 숨김 */
  posterOnlyUntilReady?: boolean;
  /** CCTV rotate_180 — 표시·포스터 180도 */
  rotate180?: boolean;
};

type Phase = 'idle' | 'loading' | 'archiving' | 'ready' | 'preview' | 'unavailable' | 'not_applicable';
type SourceKind = 'hls' | 'mp4';

function resolveNumericEventId(eventId: number | string): number | null {
  if (typeof eventId === 'number' && Number.isFinite(eventId) && eventId > 0) {
    return eventId;
  }
  const s = String(eventId).trim();
  if (/^\d+$/.test(s)) return Number(s);
  return null;
}

const RiskEvidencePlayer: React.FC<RiskEvidencePlayerProps> = ({
  eventId,
  eventType,
  cctvId,
  poolName,
  locationLabel,
  eventTimestamp,
  onStatusChange,
  embedded = false,
  minHeight = 280,
  posterImageUrl,
  posterOnlyUntilReady = false,
  rotate180 = false,
}) => {
  const theme = useTheme();
  const quietCollectingUi = Boolean(posterImageUrl);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mp4ObjectUrlRef = useRef<string | null>(null);

  const numericId = resolveNumericEventId(eventId);
  const applicable = isDrowningEventType(eventType);

  const [phase, setPhase] = useState<Phase>('idle');
  const [manifest, setManifest] = useState<RiskEvidenceManifest | null>(null);
  // 파일이 이미 회전됐으면 CSS 금지. 구버전 증거는 rotate180 prop으로만 표시 보정.
  const needDisplayRotate =
    Boolean(rotate180) && !Boolean(manifest?.rotate_applied);
  const mediaRotateSx = needDisplayRotate
    ? ({ transform: 'rotate(180deg)' } as const)
    : undefined;
  const [systemRecording, setSystemRecording] = useState<RiskRecordingSettings | null>(
    null
  );
  const [playing, setPlaying] = useState(false);
  const [errorHint, setErrorHint] = useState<string | null>(null);
  const [currentSec, setCurrentSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const normalizedPoolName = useMemo(() => {
    const raw = String(poolName || '').trim();
    if (!raw || raw === '-' || raw === '—') return '';
    return raw;
  }, [poolName]);
  const normalizedLocationLabel = useMemo(() => {
    const raw = String(locationLabel || '').trim();
    if (!raw || raw === '-' || raw === '—') return '';
    return raw;
  }, [locationLabel]);

  useEffect(() => {
    let cancelled = false;
    void fetchRiskRecordingSettingsFromSystem()
      .then((cfg) => {
        if (!cancelled) setSystemRecording(cfg);
      })
      .catch(() => {
        if (!cancelled) setSystemRecording(DEFAULT_RISK_RECORDING_SETTINGS);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cfgPre = systemRecording?.pre_seconds ?? DEFAULT_RISK_RECORDING_SETTINGS.pre_seconds;
  const cfgPost = systemRecording?.post_seconds ?? DEFAULT_RISK_RECORDING_SETTINGS.post_seconds;
  const preSec = manifest?.pre_seconds ?? manifest?.target_seconds ?? cfgPre;
  const postSec = manifest?.post_seconds ?? cfgPost;
  // HLS 세그먼트 합본(hls_segments_mp4)도 mp4_filename 이 있으면 MP4 우선
  const hasMp4 =
    manifest?.status === 'ready' &&
    (Boolean(manifest?.mp4_filename) ||
      (!manifest?.segment_count && Boolean(manifest?.playback_type)));
  const resolvedLocationLabel = normalizedLocationLabel || (cctvId != null ? `CCTV ${cctvId}` : '');
  const eventTimeMs = useMemo(() => {
    // 클립 앵커는 manifest.trigger_ts(수집 기준) 우선 — 리스트 timestamp와 어긋나면 좌상단 시각이 틀어짐
    if (manifest?.trigger_ts != null) {
      const raw = Number(manifest.trigger_ts);
      if (Number.isFinite(raw)) {
        return raw > 1e12 ? raw : raw * 1000;
      }
    }
    if (eventTimestamp != null) {
      if (typeof eventTimestamp === 'number') {
        const n = eventTimestamp > 1e12 ? eventTimestamp : eventTimestamp * 1000;
        if (Number.isFinite(n)) return n;
      } else if (eventTimestamp instanceof Date) {
        const t = eventTimestamp.getTime();
        if (Number.isFinite(t)) return t;
      } else {
        const d = new Date(eventTimestamp);
        const t = d.getTime();
        if (Number.isFinite(t)) return t;
      }
    }
    return null;
  }, [manifest?.trigger_ts, eventTimestamp]);
  const eventTimeLabel = useMemo(() => {
    if (!eventTimeMs) return '';
    const d = new Date(eventTimeMs);
    if (!Number.isFinite(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }, [eventTimeMs]);

  /** 클립 시작 실제 시각 = 감지 시각 − pre (없으면 duration 기준 추정) */
  const clipStartMs = useMemo(() => {
    if (eventTimeMs == null) return null;
    const pre = Number(preSec);
    if (Number.isFinite(pre) && pre >= 0) {
      return eventTimeMs - pre * 1000;
    }
    return eventTimeMs;
  }, [eventTimeMs, preSec]);

  const formatWallClock = useCallback((ms: number) => {
    const d = new Date(ms);
    if (!Number.isFinite(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }, []);

  const formatClipOffset = useCallback((sec: number) => {
    const s = Math.max(0, Math.floor(sec || 0));
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    return hh > 0
      ? `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
      : `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }, []);

  const wallClockNowLabel = useMemo(() => {
    if (clipStartMs == null) return '';
    return formatWallClock(clipStartMs + Math.max(0, currentSec) * 1000);
  }, [clipStartMs, currentSec, formatWallClock]);

  const wallClockEndLabel = useMemo(() => {
    if (clipStartMs == null) return '';
    const dur =
      durationSec > 0
        ? durationSec
        : Number(manifest?.duration_sec) > 0
          ? Number(manifest?.duration_sec)
          : Number(preSec) + Number(postSec);
    if (!Number.isFinite(dur) || dur <= 0) return '';
    return formatWallClock(clipStartMs + dur * 1000);
  }, [clipStartMs, durationSec, manifest?.duration_sec, preSec, postSec, formatWallClock]);

  const playbackTimeLabel = useMemo(() => {
    return `${formatClipOffset(currentSec)} / ${formatClipOffset(durationSec)}`;
  }, [currentSec, durationSec, formatClipOffset]);

  const playbackSource = useMemo<{ kind: SourceKind; url: string } | null>(() => {
    if (!numericId) return null;
    // 저장된 MP4가 있으면 우선 (세그먼트 storage_path 불일치·삭제된 스테이징 HLS 실패 방지)
    if (hasMp4) {
      return { kind: 'mp4', url: getRiskEvidenceMp4Url(numericId) };
    }
    if (manifest?.status === 'ready' && (manifest.segment_count ?? 0) > 0) {
      return { kind: 'hls', url: getRiskEvidencePlaylistUrl(numericId) };
    }
    if (manifest?.playlist_url && manifest.segment_count > 0) {
      return {
        kind: 'hls',
        url: normalizePlaybackUrl(manifest.playlist_url),
      };
    }
    return null;
  }, [numericId, manifest, hasMp4]);

  const destroyHls = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  const clearMp4ObjectUrl = useCallback(() => {
    if (mp4ObjectUrlRef.current) {
      window.URL.revokeObjectURL(mp4ObjectUrlRef.current);
      mp4ObjectUrlRef.current = null;
    }
  }, []);

  const attachSource = useCallback(
    async (source: { kind: SourceKind; url: string }) => {
      const video = videoRef.current;
      if (!video || !source?.url) return;
      destroyHls();
      clearMp4ObjectUrl();
      video.removeAttribute('src');
      video.load();
      if (source.kind === 'mp4') {
        try {
          const token = getBearerTokenForApi();
          const res = await fetch(source.url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!res.ok) throw new Error(`mp4 fetch failed (${res.status})`);
          const blob = await res.blob();
          const objectUrl = window.URL.createObjectURL(blob);
          mp4ObjectUrlRef.current = objectUrl;
          video.src = objectUrl;
          video.play().catch(() => undefined);
          setPlaying(true);
        } catch {
          setPlaying(false);
          setErrorHint('MP4 증거 영상 인증에 실패. 다시 로그인 후 시도.');
        }
        return;
      }
      const token = getBearerTokenForApi();
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          xhrSetup: (xhr) => {
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          },
        });
        hlsRef.current = hls;
        hls.attachMedia(video);
        hls.loadSource(source.url);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch(() => undefined);
          setPlaying(true);
        });
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            setErrorHint('영상 재생에 실패');
            setPlaying(false);
          }
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = source.url;
        video.play().catch(() => undefined);
        setPlaying(true);
      }
    },
    [destroyHls, clearMp4ObjectUrl]
  );

  const loadManifest = useCallback(async () => {
    if (!numericId || !applicable) {
      setPhase('not_applicable');
      return;
    }
    setPhase('loading');
    setErrorHint(null);
    try {
      const data = await fetchEventRiskClip(numericId, {
        cctv_id: cctvId,
      });
      setManifest(data);
      if (data.synthetic || data.playback_type === 'synthetic_mp4') {
        setPhase('unavailable');
        setErrorHint(
          '실영상 대신 테스트 패턴이 생성되어 증거로 표시하지 않음. 게이트웨이 HLS 또는 스트림 수집을 확인.'
        );
        return;
      }
      if (
        data.status === 'ready' &&
        (Boolean(data.mp4_filename) || (data.segment_count ?? 0) > 0)
      ) {
        setPhase('ready');
        return;
      }
      if (data.status === 'ready' && (data.playback_type || data.mp4_filename)) {
        setPhase('ready');
        return;
      }
      if (data.status === 'ready') {
        setPhase('unavailable');
        setErrorHint('증거가 저장되었지만 재생 가능한 파일 없음');
        return;
      }
      if (data.status === 'empty') {
        setPhase('unavailable');
        setErrorHint('증거 영상을 수집실패');
        return;
      }
      if ((data.segment_count ?? 0) > 0 && data.playlist_url) {
        setPhase('preview');
        return;
      }
      if (data.status === 'archiving' || data.status === 'processing') {
        setPhase('archiving');
        return;
      }
      setPhase('archiving');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        '증거 영상 정보를 불러오기 실패';
      setErrorHint(String(msg));
      setPhase('unavailable');
    }
  }, [numericId, applicable, cctvId]);

  useEffect(() => {
    loadManifest();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      destroyHls();
      clearMp4ObjectUrl();
    };
  }, [loadManifest, destroyHls, clearMp4ObjectUrl]);

  useEffect(() => {
    if (!numericId) return;
    const onEvidence = (data: { event_id?: number | string; status?: string }) => {
      if (data?.event_id == null || String(data.event_id) !== String(numericId)) return;
      const st = String(data.status || '').toLowerCase();
      if (st === 'ready' || st === 'archiving' || st === 'empty') {
        void loadManifest();
      }
    };
    monitoringWebSocket.subscribe('risk_evidence', onEvidence);
    return () => {
      monitoringWebSocket.unsubscribe('risk_evidence', onEvidence);
    };
  }, [numericId, loadManifest]);

  useEffect(() => {
    if (phase !== 'archiving' && phase !== 'loading' || !numericId) return;
    const maxWaitSec = Math.min(180, preSec + postSec + 100);
    let attempts = 0;
    const maxAttempts = Math.ceil(maxWaitSec / 3);
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const data = await fetchEventRiskClip(numericId, { cctv_id: cctvId });
        setManifest(data);
        if (data.synthetic || data.playback_type === 'synthetic_mp4') {
          setPhase('unavailable');
          setErrorHint(
            '실영상 대신 테스트 패턴이 생성되어 증거로 표시하지 않음.'
          );
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          return;
        }
        const readyWithHls = data.status === 'ready' && (data.segment_count ?? 0) > 0;
        const readyWithMp4 =
          data.status === 'ready' &&
          Boolean(data.mp4_filename || data.playback_type);
        if (readyWithHls || readyWithMp4) {
          setPhase('ready');
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          return;
        }
        if (data.status === 'empty') {
          setPhase('unavailable');
          setErrorHint('증거 영상을 수집실패');
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          return;
        }
        if ((data.segment_count ?? 0) > 0 && data.playlist_url) {
          setPhase('preview');
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          return;
        }
      } catch {
        /* retry */
      }
      if (attempts >= maxAttempts) {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
          try {
            const data = await fetchEventRiskClip(numericId, { cctv_id: cctvId });
            setManifest(data);
            const readyWithHls = data.status === 'ready' && (data.segment_count ?? 0) > 0;
            const readyWithMp4 =
              data.status === 'ready' &&
              Boolean(data.mp4_filename || data.playback_type);
            if (readyWithHls || readyWithMp4) {
              setPhase('ready');
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
            } else if (data.status === 'empty') {
              setPhase('unavailable');
              setErrorHint('증거 영상을 수집실패');
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
            } else if ((data.segment_count ?? 0) > 0 && data.playlist_url) {
              setPhase('preview');
              if (pollRef.current) clearInterval(pollRef.current);
              pollRef.current = null;
            }
          } catch {
            /* retry */
          }
        }, 10000);
      }
    }, 3000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [phase, numericId, cctvId, postSec, preSec]);

  useEffect(() => {
    if ((phase === 'ready' || phase === 'preview') && playbackSource?.url) {
      attachSource(playbackSource);
    }
  }, [phase, playbackSource, attachSource]);

  useEffect(() => {
    if (phase !== 'ready' && phase !== 'preview') return undefined;
    const v = videoRef.current;
    if (!v) return undefined;

    const syncPlaying = () => {
      setPlaying(!v.paused && !v.ended);
    };
    const onTime = () => {
      setCurrentSec(v.currentTime || 0);
      if (v.ended) setPlaying(false);
    };
    const onMeta = () => setDurationSec(Number.isFinite(v.duration) ? v.duration : 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => {
      setPlaying(false);
      setCurrentSec(Number.isFinite(v.duration) ? v.duration : v.currentTime || 0);
    };

    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('durationchange', onMeta);
    v.addEventListener('play', onPlay);
    v.addEventListener('playing', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnd);
    syncPlaying();

    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('durationchange', onMeta);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('playing', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnd);
    };
  }, [phase, playbackSource?.url]);

  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      const v = videoRef.current;
      if (!v) return;
      setCurrentSec(v.currentTime || 0);
      if (Number.isFinite(v.duration)) setDurationSec(v.duration);
      if (v.ended || v.paused) {
        setPlaying(false);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [playing]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.ended) {
      v.currentTime = 0;
      setCurrentSec(0);
      v.play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
      return;
    }
    if (v.paused) {
      v.play()
        .then(() => setPlaying(true))
        .catch(() => setPlaying(false));
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const downloadEvidence = async () => {
    if (!numericId) return;
    try {
      const token = getBearerTokenForApi();
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      let blob: Blob | null = null;
      let filename = `risk-evidence-event-${numericId}.zip`;
      const zipRes = await fetch(getRiskEvidenceDownloadUrl(numericId), {
        headers: authHeaders,
      });
      if (zipRes.ok) {
        blob = await zipRes.blob();
      } else {
        const mp4Res = await fetch(getRiskEvidenceMp4Url(numericId), {
          headers: authHeaders,
        });
        if (!mp4Res.ok) throw new Error('다운로드 실패');
        blob = await mp4Res.blob();
        filename = `risk-evidence-event-${numericId}.mp4`;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setErrorHint('증거 영상 다운로드에 실패');
    }
  };

  useEffect(() => {
    if (!onStatusChange) return;
    if (phase === 'ready') onStatusChange('ready');
    else if (phase === 'archiving') onStatusChange('archiving');
    else if (phase === 'preview') onStatusChange('preview');
    else if (phase === 'not_applicable') onStatusChange('not_applicable');
    else if (phase === 'unavailable') onStatusChange('unavailable');
  }, [phase, onStatusChange]);

  if (!applicable) {
    return null;
  }

  const accent = theme.palette.error.main;
  const posterSnapshotMode = Boolean(
    posterImageUrl &&
      quietCollectingUi &&
      (phase === 'idle' || phase === 'loading' || phase === 'archiving')
  );
  const shellSx = embedded
    ? { width: '100%' }
    : {
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(accent, 0.35)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.35)}`,
        background: `linear-gradient(145deg, ${alpha(accent, 0.12)} 0%, ${alpha(
          theme.palette.background.paper,
          0.95
        )} 45%)`,
      };

  return (
    <Box sx={shellSx}>
      {!posterSnapshotMode && (
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MovieIcon sx={{ color: accent }} />
          <Typography variant="subtitle1" fontWeight={700}>
            증거 영상
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" label={`앞 ${preSec}초`} variant="outlined" />
          <Chip size="small" label={`뒤 ${postSec}초`} variant="outlined" />
          {manifest?.segment_count != null && manifest.segment_count > 0 && (
            <Chip
              size="small"
              color="primary"
              label={`${manifest.segment_count}개 세그먼트`}
            />
          )}
          {phase === 'ready' && (
            <Chip size="small" color="success" label="보관 완료" />
          )}
          {phase === 'preview' && (
            <Chip size="small" color="warning" label="라이브 미리보기" />
          )}
          {(phase === 'loading' || phase === 'archiving') && !quietCollectingUi && (
            <Chip size="small" color="info" label="영상 수집 중" />
          )}
        </Box>
      </Box>
      )}

      <Box
        sx={{
          position: 'relative',
          mx: embedded ? 0 : 2,
          my: posterSnapshotMode ? 0 : 2,
          borderRadius: 1.5,
          overflow: 'hidden',
          bgcolor: '#0a0e14',
          minHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {posterSnapshotMode && (
          <Box
            component="img"
            src={posterImageUrl}
            alt="감지 시점"
            sx={{
              width: '100%',
              height: '100%',
              minHeight,
              objectFit: 'contain',
              display: 'block',
              ...mediaRotateSx,
            }}
          />
        )}

        {!quietCollectingUi && (phase === 'loading' || phase === 'archiving') && (
          <>
            {posterImageUrl ? (
              <Box
                component="img"
                src={posterImageUrl}
                alt="감지 시점"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  ...mediaRotateSx,
                }}
              />
            ) : null}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                px: 2,
                bgcolor: alpha('#000', posterImageUrl ? 0.52 : 0.75),
              }}
            >
              <HourglassEmptyIcon sx={{ fontSize: 44, color: 'rgba(255,255,255,0.9)' }} />
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600 }}>
                증거 영상을 수집하는 중…
              </Typography>
              <LinearProgress
                sx={{ width: 'min(320px, 85%)', height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)', textAlign: 'center' }}>
                감지 시점 전후 구간을 게이트웨이에서 모으고 있음. 이 창을 닫아도 서버에서는 수집이
                계속됨
              </Typography>
            </Box>
          </>
        )}

        {!posterSnapshotMode && (phase === 'ready' || phase === 'preview') && (
          <>
            <video
              ref={videoRef}
              playsInline
              muted
              controls={false}
              style={{
                width: '100%',
                height: '100%',
                minHeight,
                objectFit: 'contain',
                display: 'block',
                transform: needDisplayRotate ? 'rotate(180deg)' : undefined,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              {(normalizedPoolName || resolvedLocationLabel) && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 700 }}>
                  {[normalizedPoolName, resolvedLocationLabel].filter(Boolean).join(' · ')}
                </Typography>
              )}
              {eventTimeLabel && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.92)' }}>
                  이벤트 발생: {eventTimeLabel}
                </Typography>
              )}
              {wallClockNowLabel ? (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 600 }}>
                  영상 시각: {wallClockNowLabel}
                </Typography>
              ) : null}
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.78)' }}>
                클립 위치: {playbackTimeLabel}
                {wallClockEndLabel ? ` · 끝 ${wallClockEndLabel}` : ''}
              </Typography>
              {manifest?.playback_type === 'snapshot_still' && (
                <Typography variant="caption" sx={{ color: 'rgba(255,240,170,0.95)' }}>
                  실시간 영상 미수집 — 이벤트 캡처로 만든 정지 영상.
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                background: `linear-gradient(180deg, ${alpha('#000', 0.45)} 0%, transparent 28%, transparent 72%, ${alpha(
                  '#000',
                  0.5
                )} 100%)`,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                right: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pointerEvents: 'auto',
              }}
            >
              {playbackSource?.kind !== 'mp4' ? (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  게이트웨이 HLS · 감지 시점 전후
                </Typography>
              ) : (
                <span />
              )}
              <Box>
                <Tooltip title="증거 ZIP 다운로드">
                  <span>
                    <IconButton
                      onClick={downloadEvidence}
                      disabled={phase !== 'ready'}
                      sx={{ color: 'white', mr: 0.5 }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={playing ? '일시정지' : '재생'}>
                  <IconButton
                    onClick={togglePlay}
                    sx={{
                      color: 'white',
                      bgcolor: alpha(accent, 0.85),
                      '&:hover': { bgcolor: accent },
                    }}
                  >
                    {playing ? <PauseIcon /> : <PlayArrowIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="다시 불러오기">
                  <IconButton
                    onClick={() => {
                      destroyHls();
                      loadManifest();
                    }}
                    sx={{ color: 'white', ml: 0.5 }}
                  >
                    <ReplayIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </>
        )}

        {!posterSnapshotMode && phase === 'unavailable' && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <CloudOffIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {errorHint || '증거 영상을 표시 불가'}
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
              게이트웨이·녹화 설정·HLS 업로드 상태를 확인
            </Typography>
          </Box>
        )}
      </Box>

    </Box>
  );
};

export default RiskEvidencePlayer;
